import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Save, Plus, Trash2, Edit, X, Eye, EyeOff, Loader2, Image as ImageIcon,
  Star, Mail, User, Phone, Lock, Shield, FileText, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReferrerModal = ({ referrer, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    name: referrer?.name || '',
    email: referrer?.email || '',
    phone: referrer?.phone || '',
    company: referrer?.company || '',
    referrer_type: referrer?.referrer_type || 'individual',
    commission_rate: referrer?.commission_rate || 0,
    notes: referrer?.notes || '',
    active: referrer?.active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between border-b sticky top-0 bg-white z-10">
          <CardTitle>{referrer ? 'Edit Referrer' : 'New Referrer'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="John Smith"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#D4AF37]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#D4AF37]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="204-555-0123"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#D4AF37]"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Company (Optional)</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#D4AF37]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Referrer Type</label>
                <select
                  value={formData.referrer_type}
                  onChange={(e) => setFormData({ ...formData, referrer_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#D4AF37]"
                >
                  <option value="individual">Individual</option>
                  <option value="realtor">Realtor</option>
                  <option value="lawyer">Lawyer</option>
                  <option value="accountant">Accountant</option>
                  <option value="company">Company</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Commission Rate (%)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.commission_rate}
                onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#D4AF37]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
                placeholder="Any additional notes..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#D4AF37]"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="referrer-active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-[#D4AF37] rounded"
              />
              <label htmlFor="referrer-active" className="text-sm font-medium">Active (can log in)</label>
            </div>

            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
              <strong>Note:</strong> When you create a referrer, login credentials will be auto-generated and emailed to them. They can access their portal at <code>/referrer-login</code>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {referrer ? 'Update Referrer' : 'Create Referrer'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
